import React from 'react';
import { Link } from 'react-router-dom';

export default function ForgotPassword() {
  const [email, setEmail] = React.useState('');
  const [status, setStatus] = React.useState('idle');
  const [errorMessage, setErrorMessage] = React.useState('');
  const [code, setCode] = React.useState('');
  const [newPassword, setNewPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');

  const handleSendCode = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMessage('');

    try {
      const result = await window.Clerk.client.signIn.create({
        strategy: 'reset_password_email_code',
        identifier: email,
      });

      if (result.status === 'needs_first_factor') {
        setStatus('sent');
      }
    } catch (err) {
      console.error('Password reset error:', err);
      setStatus('error');
      
      if (err.errors && err.errors[0]) {
        const clerkError = err.errors[0];
        if (clerkError.code === 'form_identifier_not_found') {
          setErrorMessage('No account found with this email address.');
        } else {
          setErrorMessage(clerkError.message || 'Unable to send reset email. Please try again.');
        }
      } else {
        setErrorMessage('Unable to send reset email. Please try again.');
      }
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    if (newPassword !== confirmPassword) {
      setErrorMessage('Passwords do not match.');
      return;
    }

    if (newPassword.length < 8) {
      setErrorMessage('Password must be at least 8 characters.');
      return;
    }

    setStatus('loading');

    try {
      const result = await window.Clerk.client.signIn.attemptFirstFactor({
        strategy: 'reset_password_email_code',
        code: code,
        password: newPassword,
      });

      if (result.status === 'complete') {
        await window.Clerk.setActive({ session: result.createdSessionId });
        window.location.href = '/dashboard';
      }
    } catch (err) {
      console.error('Password reset error:', err);
      setStatus('sent');
      
      if (err.errors && err.errors[0]) {
        const clerkError = err.errors[0];
        if (clerkError.code === 'form_code_incorrect') {
          setErrorMessage('Invalid verification code. Please check and try again.');
        } else if (clerkError.code === 'form_password_pwned') {
          setErrorMessage('This password has been found in a data breach. Please choose a different one.');
        } else {
          setErrorMessage(clerkError.message || 'Unable to reset password. Please try again.');
        }
      } else {
        setErrorMessage('Unable to reset password. Please try again.');
      }
    }
  };

  const styles = {
    container: {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
      padding: '24px',
    },
    card: {
      background: 'white',
      borderRadius: '16px',
      padding: '40px',
      maxWidth: '420px',
      width: '100%',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 20px 25px -5px rgba(0, 0, 0, 0.1)',
    },
    logoSection: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '32px',
      justifyContent: 'center',
      flexDirection: 'column',
    },
    logoImage: {
      height: '60px',
      width: 'auto',
    },
    logoText: {
      fontSize: '1.5rem',
      fontWeight: '700',
      color: '#2C3E50',
      margin: 0,
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: '600',
      color: '#1f2937',
      marginBottom: '8px',
      textAlign: 'center',
    },
    subtitle: {
      fontSize: '0.95rem',
      color: '#6b7280',
      marginBottom: '24px',
      textAlign: 'center',
      lineHeight: '1.5',
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
    },
    inputGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
    },
    label: {
      fontSize: '0.875rem',
      fontWeight: '500',
      color: '#374151',
    },
    input: {
      padding: '12px 16px',
      fontSize: '1rem',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      outline: 'none',
      width: '100%',
      boxSizing: 'border-box',
    },
    button: {
      padding: '14px 24px',
      fontSize: '1rem',
      fontWeight: '600',
      color: 'white',
      background: '#1E40AF',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      marginTop: '8px',
    },
    buttonDisabled: {
      background: '#9ca3af',
      cursor: 'not-allowed',
    },
    linkButton: {
      padding: '8px',
      fontSize: '0.9rem',
      color: '#6b7280',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      textAlign: 'center',
    },
    errorBox: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '12px 16px',
      background: '#fef2f2',
      border: '1px solid #fecaca',
      borderRadius: '8px',
      color: '#dc2626',
      fontSize: '0.875rem',
    },
    footer: {
      marginTop: '24px',
      paddingTop: '24px',
      borderTop: '1px solid #e5e7eb',
      textAlign: 'center',
    },
    link: {
      color: '#1E40AF',
      textDecoration: 'none',
      fontSize: '0.9rem',
      fontWeight: '500',
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.logoSection}>
          <img
            src="/Housecalclogo.png"
            alt="Estimate Assistant"
            style={styles.logoImage}
          />
          <h1 style={styles.logoText}>Estimate Assistant</h1>
        </div>

        {(status === 'idle' || status === 'error') && (
          <>
            <h2 style={styles.title}>Reset your password</h2>
            <p style={styles.subtitle}>
              Enter your email address and we'll send you a verification code.
            </p>

            <form onSubmit={handleSendCode} style={styles.form}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Email address</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  required
                  style={styles.input}
                />
              </div>

              {errorMessage && (
                <div style={styles.errorBox}>
                  <span>{errorMessage}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={status === 'loading'}
                style={{
                  ...styles.button,
                  ...(status === 'loading' ? styles.buttonDisabled : {}),
                }}
              >
                {status === 'loading' ? 'Sending...' : 'Send reset code'}
              </button>
            </form>
          </>
        )}

        {status === 'sent' && (
          <>
            <h2 style={styles.title}>Check your email</h2>
            <p style={styles.subtitle}>
              We sent a code to <strong>{email}</strong>
            </p>

            <form onSubmit={handleResetPassword} style={styles.form}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Verification code</label>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="Enter 6-digit code"
                  required
                  maxLength={6}
                  style={{ ...styles.input, textAlign: 'center', letterSpacing: '0.5em', fontSize: '1.25rem' }}
                />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>New password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  required
                  minLength={8}
                  style={styles.input}
                />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Confirm password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your new password"
                  required
                  style={styles.input}
                />
              </div>

              {errorMessage && (
                <div style={styles.errorBox}>
                  <span>{errorMessage}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={status === 'loading'}
                style={{
                  ...styles.button,
                  ...(status === 'loading' ? styles.buttonDisabled : {}),
                }}
              >
                {status === 'loading' ? 'Resetting...' : 'Reset password'}
              </button>

              <button
                type="button"
                onClick={() => {
                  setStatus('idle');
                  setCode('');
                  setNewPassword('');
                  setConfirmPassword('');
                  setErrorMessage('');
                }}
                style={styles.linkButton}
              >
                ← Use a different email
              </button>
            </form>
          </>
        )}

        <div style={styles.footer}>
          <Link to="/sign-in" style={styles.link}>
            ← Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}